import { makeStyles } from '@material-ui/styles';
import type { Theme } from '@bizzabo/design-system';
import { transparentize } from 'polished';

export const useStyles = makeStyles<Theme>(
    theme => ({
        header: {
            width: '100%',
            zIndex: 1001,
            display: 'none',
            backgroundColor: theme.customization.background.C10,
            padding: '0 4% 20px 4%',
            flexWrap: 'wrap',
            flexShrink: 0, // fixing collapsed vertical paddings
            boxShadow: `-12px 0px 0px 0px ${theme.customization.background.C10}, 12px 0px 0px 0px ${theme.customization.background.C10}`,
            [theme.breakpoints.up('sm')]: {
                display: 'flex',
                padding: '0 5% 20px 5%',
            },
        },
        appToolsHeader: {
            padding: '0',
            [theme.breakpoints.up('sm')]: {
                display: 'flex',
                position: 'sticky',
                top: 0,
                transition: '0.3s ease-in-out',
            },
        },
        hideHeader: {
            top: -94,
            transition: '0.3s ease-in-out',
        },
        headerWrapper: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            borderBottom: `var(--header-border-bottom) solid ${theme.customization.background.C30}`,
            padding: 'var(--header-padding-top) 0 var(--header-padding-bottom)',
        },
        navigationUl: {
            listStyleType: 'none',
            display: 'flex',
            alignItems: 'center',
            margin: 0,
            paddingInlineStart: 0,
            '& > li': {
                display: 'flex',
                whiteSpace: 'nowrap',
            },
            '& li:nth-of-type(1) a': {
                marginLeft: 53,
                [theme.breakpoints.down('md')]: {
                    marginLeft: 20,
                },
            },
        },
        imageWrapper: {
            height: 'var(--header-logo-height)',
            maxHeight: 'var(--header-logo-height)',
            maxWidth: 160,
            marginRight: 14,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        eventLogo: {
            display: 'inline-block',
            maxHeight: 'var(--header-logo-height)',
            maxWidth: 160,
        },
        emptyBlock: {
            minHeight: 'var(--header-logo-height)',
            maxWidth: 160,
        },
        link: {
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            padding: '6px 10px',
            borderRadius: 8,
            fontFamily: 'La Nord',
            fontSize: '2.1rem',
            fontWeight: 400,
            lineHeight: 1.35,
            color: `${transparentize(0.5, theme.customization.background.G10)}`,
            textDecoration: 'none',
            transition: 'all 300ms ease',
            '&a': {
                fontSize: '2.1rem',
            },
            '&:hover:not(.active)': {
                color: theme.customization.background.G10,
                '&::after': {
                    transform: 'scaleX(1)',
                },
            },
            '&:not(.active)': {
                '&::after': {
                    transition: 'all 300ms ease',
                    position: 'absolute',
                    content: '""',
                    height: 1,
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    backgroundColor: theme.customization.background.G10,
                    bottom: -21,
                    left: 0,
                    right: 0,
                },
            },
            marginLeft: 22,
            marginRight: 22,
            cursor: 'pointer',
            [theme.breakpoints.down('md')]: {
                fontSize: 18,
                padding: '6px 8px',
                marginLeft: 8,
                marginRight: 8,
                '&.nav-link:not(.active)::after': {
                    bottom: -23,
                },
            },
        },
        linkActive: {
            transition: 'all 300ms ease',
            color: theme.customization.background.G10,
            '&::after': {
                transition: 'all 300ms ease',
                position: 'absolute',
                transformOrigin: 'left',
                transform: 'scaleX(1)',
                content: '""',
                height: 3,
                backgroundColor: theme.customization.background.G10,
                bottom: -22,
                left: 0,
                right: 0,
            },
            [theme.breakpoints.down('md')]: {
                '&::after': {
                    bottom: -24,
                },
            },
        },
        visitWebsiteButton: {
            paddingBottom: 0,
            margin: '12px 0',
        },
        actions: {
            display: 'flex',
            alignItems: 'center',
            marginLeft: 'auto',
            '& > * + *': {
                marginLeft: 16,
            },
            '& button > * + *': {
                // temporary solution to prevent layout being broken
                // till we will come up to the solution with the toolbar tool
                '@media (max-width: 792px ) and (min-width: 692px)': {
                    display: 'none',
                },
            },
        },
        hiddenOnMedium: {
            [theme.breakpoints.down('md')]: {
                display: 'none',
            },
        },
        hiddenOnLarge: {
            [theme.breakpoints.up('md')]: {
                display: 'none',
            },
        },
        userProfileWrapper: {
            display: 'flex',
            alignItems: 'center',
            '& > div:hover': {
                cursor: 'pointer',
            },
        },
        arrowIcon: {
            transform: 'scale(0.9) translate(6px, 3px)',
        },
        agendaTopBar: {
            '&#agenda-second-navigation': {
                top: 113,
                transition: '0.3s ease-in-out',
                [theme.breakpoints.down('sm')]: {
                    top: 50,
                },
            },
        },
        hideAgendaTopBar: {
            transition: '0.3s ease-in-out',
            transform: 'translateY(-220%)',
        },
    }),
    { classNamePrefix: 'drawer-navigation-lg-md' }
);
